import { computed, inject, Injectable, signal } from "@angular/core";
import { UtilityService } from "@eforall/common";
import { DialogService } from "@eforall/dashboard";
import { AccApplicationDialogFuncData, AccApplicationStatusId, Accelerator, WeekdayIndex } from "@interfaces";
import { canAttendAcc } from "../../../pages/helpers/calculate-leads-or-acc-applications-score";
import { AppService, DomainDataService, FuncService } from "../../../services";
import { ApplicationInterviewFormValues } from "./interview/interview-form.service";

interface AccWithScheduleSlots extends Accelerator {
	dayIndex: WeekdayIndex,
	startTimeIndex: number,
	applicantSelectedAccSlot: boolean,
}
@Injectable({ providedIn: 'root' })
export class AccApplicationDialogDataService {

	private func = inject(FuncService);
	public util = inject(UtilityService);
	public app = inject(AppService);
	public dialogService = inject(DialogService);
	public domain = inject(DomainDataService);
	public readonly dialogData = signal<AccApplicationDialogFuncData | undefined>(undefined);
	public readonly accWithScheduleSlots = signal<AccWithScheduleSlots[]>([]);
	public readonly isReadonly = computed(() => !this.app.data().permissions.canEnrollEntrepreneurs);


	public async loadData(accApplicationId: number) {
		if (!accApplicationId) {
			console.error(`No acc Application Id passed in`);
			this.dialogData.set(undefined);
			return;
		}

		const data = await this.func.accApplication.getDialogData({ accApplicationId });
		if (data) this.mapAcceleratorScheduleDetails(data);
		this.dialogData.set(data);
	}

	public async reloadData() {
		const dialogData = this.dialogData();
		if (!dialogData?.details?.accApplicationId) {
			console.error(`No acc Application Id found in dialog data`);
			this.dialogData.set(undefined);
			return;
		};

		const data = await this.func.accApplication.getDialogData({ accApplicationId: dialogData.details.accApplicationId });
		if (data) this.mapAcceleratorScheduleDetails(data);
		this.dialogData.set(data);
	}


	/**
	 * Changes the application status and logs the change.
	 * If the application has an offered seat and the new status is AcceptPending or higher,
	 * prompts the user for confirmation and removes the offered seat ad sets the status.
	 * Otherwise, directly changes the status and logs it.
	 * Reloads data after a successful status change.
	 * @param accApplicationId The application ID.
	 * @param toAccApplicationStatusId The target status ID.
	 */
	public async changeAccApplicationStatusAndLog(accApplicationId: number, toAccApplicationStatusId: number, notes?: string, deferApplication: boolean = false) {
		//
		// No access - cannot update
		//
		if (this.isReadonly()) return;

		const accApplication = this.dialogData()?.details;
		if (!accApplication || !accApplicationId || !toAccApplicationStatusId) return;

		const currentStatusId = accApplication.accApplicationStatusId;
		const offeredSeatAccId = accApplication.offeredSeatAccId;
		let success = false;

		const openStatuses = this.domain.data().accApplicationStatuses.filter(status => status.isOpen).map(status => status.accApplicationStatusId);
		const toStatus = this.domain.data().accApplicationStatusesMap[toAccApplicationStatusId]?.name || `statusId:${toAccApplicationStatusId}`;
		const fromStatus = this.domain.data().accApplicationStatusesMap[currentStatusId]?.name || `statusId:${currentStatusId}`;


		// If the application has an offered seat and the status is AcceptPending or higher, prompt and remove seat
		if (currentStatusId >= AccApplicationStatusId.SeatOffered && offeredSeatAccId) {

			// Show confirmation dialog
			const yes = await this.dialogService.confirm(`Are you sure you want to change the application status from '${fromStatus}' to '${toStatus}'? This will remove the assigned seat and this action cannot be undone.`, 'Yes', 'No', [], 400, 250);

			if (!yes) return; // User cancelled

			// Remove offered seat and change status in one call
			const removeResult = await this.func.accApplication.removeOfferedSeatAccIdAndChangeStatus({ accApplicationId: accApplication.accApplicationId, toStatusId: toAccApplicationStatusId });
			if (removeResult.errorMessages?.length) {
				const message = `Cannot remove accelerator - accId:${offeredSeatAccId} seat for the application for the following reason(s):`;
				await this.dialogService.showMessage('Error', message, 'OK', removeResult.errorMessages);
				return;
			}
			else success = true;
		}
		// If the application is to be defered but the status selectes is an Open Status,
		// show confirm dialog and remove deferred flag
		else if (deferApplication && openStatuses.includes(toAccApplicationStatusId)) {

			const yes = await this.dialogService.confirm(`Are you sure you want to change the application status from '${fromStatus}' to '${toStatus}' ? This will remove the deferement of the application.`, 'Yes', 'No', [], 400, 250);

			if (!yes) return; // User cancelled

			// Remove isDeferred flag and change status in one call
			const removeResult = await this.func.accApplication.removeDeferementAndChangeStatus({ accApplicationId: accApplication.accApplicationId, toStatusId: toAccApplicationStatusId });
			if (removeResult.errorMessages?.length) {
				const message = `Cannot change status and remove deferement for the application for the following reason:`;
				await this.dialogService.showMessage('Error', message, 'OK', removeResult.errorMessages);
				return;
			}
			else success = true;
		} else {
			// Directly change status and log
			const logId = await this.func.accApplication.addAccApplicationStatusDeferementAndLog({ accApplicationId, toAccApplicationStatusId, notes, deferApplication });
			success = true;
		}

		if (success) {
			await this.reloadData();
		}
	}

	private mapAcceleratorScheduleDetails(dialogFuncData: AccApplicationDialogFuncData) {

		const AccWithScheduleSlots = dialogFuncData.accelerators.map(acc => {
			const { dayIndex, startTimeIndex, acceleratorDisplayTime, localDisplayTime } = this.util.date.formatAccMeetingSlot(acc.meetingStartSlot, acc.standardFourTimeZoneId, true, this.app.data().standardLocalTimezoneId);
			return {
				...acc,
				meetingAcceleratorDisplayText: acceleratorDisplayTime,
				meetingLocalDisplayText: localDisplayTime,
				dayIndex,
				startTimeIndex,
				applicantSelectedAccSlot: dialogFuncData.details.availMeetings ? canAttendAcc(dayIndex, startTimeIndex, dialogFuncData.details.availMeetings.selectedMeetingSlots) : false,
			};
		});

		this.accWithScheduleSlots.set(AccWithScheduleSlots);
	}


	/**
	 *  Sets the offered seat accelerator ID for the application.
	 * It will show a message if there are any error messages returned from the func call.
	 * If successful, it will reload the data for the application.
	 * @param offeredAccId 
	 * @returns 
	 */
	public async confirmAndAssignOfferedSeat(offeredAccId: number, availableSeatCount: number) {
		//
		// No access - cannot update
		//
		if (this.isReadonly()) return;

		const accApplicationId = this.dialogData()?.details.accApplicationId;
		if (!accApplicationId) return;

		let confirmationMessage = `Are you sure you want to offer seat accId - ${offeredAccId} ?`;
		if (availableSeatCount === 0) {
			confirmationMessage = `ALL SEATS ARE ALREADY OFFERED TO accId - ${offeredAccId}. By continuing to offer an additional seat you will need to remove a seat already offered. Only do this if ABSOLUTELY NECESSARY. Are you sure you would like to continue?`;
		}

		const yes = await this.dialogService.confirm(confirmationMessage, 'Yes', 'No', [], 400, 250);
		if (yes) {

			const result = await this.func.accApplication.setOfferedSeatAccId({ accApplicationId, offeredAccId });
			if (result.errorMessages?.length) {
				const message = `Cannot offer accelerator - accId:${offeredAccId} seat for the application for the following reason(s):`;
				await this.dialogService.showMessage('Error', message, 'OK', result.errorMessages);
				return false;
			}
			else {
				await this.reloadData();
				return true;
			}
		}
		return false;

	}


	/**
	 *  Removes the offered seat accelerator from the application.
	 *  It will show a confirmation dialog before proceeding with the removal.
	 *  If the removal is successful, it will reload the data for the application.
	 */
	public async confirmAndRemoveOfferedSeatAccId() {
		//
		// No access - cannot update
		//
		if (this.isReadonly()) return;

		const accApplication = this.dialogData()?.details;
		if (!accApplication) return false;

		if (!accApplication.offeredSeatAccId) {
			await this.dialogService.showMessage('No Offered Seat', 'Application does not have an offered seat to remove.');
			return false;
		}

		const yes = await this.dialogService.confirm(`Are you sure you want to remove offered seat accId - ${accApplication.offeredSeatAccId} ? This action cannot be undone.`, 'Yes', 'No', [], 400, 200);

		if (yes) {
			const result = await this.func.accApplication.removeOfferedSeatAccIdAndChangeStatus({ accApplicationId: accApplication.accApplicationId });
			if (result.errorMessages?.length) {
				const message = `Cannot remove accelerator - accId:${accApplication.offeredSeatAccId} seat for the application for the following reason(s):`;
				await this.dialogService.showMessage('Error', message, 'OK', result.errorMessages);
				return false;
			}
			else {
				await this.reloadData();
				return true;
			}
		}
		return false;

	}



	public async addOrUpdateInterview(values: ApplicationInterviewFormValues) {
		//
		// No access - cannot update
		//
		const accApplicationId = this.dialogData()?.details.accApplicationId;

		if (this.isReadonly() || !accApplicationId) return;

		const accApplicationInterview = this.dialogData()?.details.interview;

		if (!accApplicationInterview || (
			values.valuePropositionRating != accApplicationInterview.valuePropositionRating ||
			values.sustainabilityRating != accApplicationInterview.sustainabilityRating ||
			values.coachabilityRating != accApplicationInterview.coachabilityRating ||
			values.timeCommitmentRating != accApplicationInterview.timeCommitmentRating ||
			values.notes != accApplicationInterview.notes
		)) {
			await this.func.accApplicationInterview.addOrUpdate({
				accApplicationId,
				coachabilityRating: values.coachabilityRating,
				notes: values.notes,
				sustainabilityRating: values.sustainabilityRating,
				timeCommitmentRating: values.timeCommitmentRating,
				valuePropositionRating: values.valuePropositionRating
			});

			await this.reloadData();
		}

	}
}